import type { PassiveDef } from '~/data/passives'

/**
 * 🧬 Per-creature passives (v2.80) — the creature expansion's core layer.
 *
 * The five elemental passives in `passives.ts` are awarded by NATURE: any
 * fire-topped creature awakens Kindled Resolve. These belong to one
 * specific creature instead, named on `CreatureDef.passive`. Javi's rule
 * is one set or the other, never both — a creature with a passive here
 * does NOT also get its elemental one (see `creaturePassive`).
 *
 * Tier pacing lives in `unlockLevels`, so a passive can bloom early or
 * make the player wait. Apotheosis is always one extra tier two levels
 * past the last natural one.
 *
 * DOC NOTE (settled v2.81, per Javi): in the proposal the bold numbers
 * after a passive name are the LEVELS its tiers unlock at; the magnitudes
 * live in the description. Several ladders coincide with their own
 * magnitudes (Grit unlocks at 2/4/6 AND grants +2/+4/+6) — that is
 * deliberate, not a transcription slip. Every ladder below is copied
 * verbatim from the reviewed doc.
 */
export const CREATURE_PASSIVES: Record<string, PassiveDef> = {
  // 🦕 v5.95: Legacy's engine (kit locked by Javi). Tier texts are
  // the contract; effects live in utils/battle (burdenOfAgesOnSuccess,
  // steadyGroundExpiryMult). Apotheosis pinned to 10 per the design.
  'burden-of-ages': {
    id: 'burden-of-ages',
    name: 'Burden of Ages',
    icon: '🏔️',
    image: '/ui/passives/earth.webp',
    unlockLevels: [1, 5, 8],
    apotheosisAt: 10,
    tiers: [
      'Every mission it completes grants +1 permanent point in EACH element that mission required.',
      'The growth doubles: +2 permanent points in each required element, every mission.',
      'STEADY GROUND: while it waits at basecamp, waiting missions expire 50% slower.',
      'APOTHEOSIS — GAIA’S GIFT: while it stays at camp, every OTHER creature also earns +1 per required element on its missions.',
    ],
  },
  // 🎩 v6.88: Mr. Khool (homage kit, settled 2026-09-10). Tier texts are
  // the contract; the rerolls live in utils/battle (composureRescue), the
  // Apotheosis in computeMissionOutcome. Ladder 1/8/15, Apotheosis 17.
  composure: {
    id: 'composure',
    name: 'Composure',
    icon: '🎩',
    image: '/ui/passives/water.webp',
    unlockLevels: [1, 8, 15],
    apotheosisAt: 17,
    tiers: [
      'A failed roll on a mission he joins is rolled again, once - and the miss still pays anything that rewards a failure.',
      'Rolled again up to twice.',
      'Rolled again up to three times.',
      'APOTHEOSIS — COOL HEAD: a mission he joins that still fails after every reroll deals half damage.',
    ],
  },
  // 🧤 v6.89: Aid (homage kit, settled 2026-09-10). His stats are read
  // in the shape the mission wants (anticipationRelabel in utils/battle);
  // the pairing rule lives in slotCapacity. Ladder 1/4/7, Apotheosis 10.
  anticipation: {
    id: 'anticipation',
    name: 'Anticipation',
    icon: '🧤',
    image: '/ui/passives/lightning.webp',
    unlockLevels: [1, 4, 7],
    apotheosisAt: 10,
    tiers: [
      'He joins any mission Mr. Khool is on regardless of its slots, and his highest element counts as whichever element the mission demands most.',
      'His top two elements count as the two elements the mission demands most.',
      'His whole statline is read in the shape of the mission\'s requirements, best to most demanded.',
      'APOTHEOSIS — AT YOUR SERVICE: when they ride together, Mr. Khool\'s statline is read the same way.',
    ],
  },
  // 🕊️ v6.90: The Noble One (homage kit, settled 2026-09-10). The Ascetic's
  // reward, so the kit IS that discipline, as a meter: silenceTick /
  // breakSilence / silenceBonus in utils/battle. Ladder 2/5/8, Apotheosis 10.
  'the-long-silence': {
    id: 'the-long-silence',
    name: 'The Long Silence',
    icon: '🕊️',
    image: '/ui/passives/lightning.webp',
    unlockLevels: [2, 5, 8],
    apotheosisAt: 10,
    tiers: [
      'Every 10 seconds in which nobody arms a surge or fires an on-use, the whole team gains a stack of Silence: +1 to every element, on everyone, up to 5. Any press clears it all.',
      'Silence can climb to 10 stacks.',
      'Silence can climb to 20 stacks.',
      'APOTHEOSIS — THE FIRST WORD: the stacks survive the first press each node.',
    ],
  },
  // 🌌 v6.91: Lanfeust (homage kit, settled 2026-09-10). Worth more parked
  // than dispatched - the opposite of every other creature. gravityTick in
  // utils/battle. Ladder 3/6/9, Apotheosis 10.
  gravity: {
    id: 'gravity',
    name: 'Gravity',
    icon: '🌌',
    image: '/ui/passives/lightning.webp',
    unlockLevels: [3, 6, 9],
    apotheosisAt: 10,
    tiers: [
      'While he waits at basecamp, every waiting mission loses 1 point from its highest requirement every 15 seconds (bosses stand firm).',
      'The board bends every 10 seconds.',
      'The board bends every 5 seconds.',
      'APOTHEOSIS — EVENT HORIZON: the pull continues while he is deployed.',
    ],
  },
  // 🏗️ v6.92: Robodude (Javi's design, settled 2026-09-10). Two passives
  // in one ladder: Hold Position (utils/battle stationCreature, the
  // dispatch leg in useEncounter) and Overkill (computeMissionOutcome).
  // Self-scoped, pure numbers, nobody teleports. Ladder 1/4/7, Apotheosis 10.
  'siege-engine': {
    id: 'siege-engine',
    name: 'Siege Engine',
    icon: '🏗️',
    image: '/ui/passives/earth.webp',
    unlockLevels: [1, 4, 7],
    apotheosisAt: 10,
    tiers: [
      'HOLD POSITION: he never walks home. He stands where his mission was, keeps that site clear of new missions, and his next dispatch measures travel from where he stands. OVERKILL: a mission he joins that lands at 150% coverage or more pays him +1 permanent stat point and the team double XP.',
      'Overkill from 130% coverage.',
      'Overkill from 110% coverage.',
      'APOTHEOSIS — SIEGE WORKS: Overkill pays +2 permanent stat points.',
    ],
  },
  'morning-chorus': {
    id: 'morning-chorus',
    name: 'Morning Chorus',
    icon: '🌅',
    image: '/ui/passives/lightning.webp',
    unlockLevels: [2, 4, 6],
    tiers: [
      'The first mission each encounter gets +10s before expiring.',
      'The first 2 missions each encounter get +10s before expiring.',
      'The first 3 missions each encounter get +10s before expiring.',
      'APOTHEOSIS: EVERY mission each encounter gets +6s, not just the first three.',
    ],
  },
  ashbed: {
    id: 'ashbed',
    name: 'Ashbed',
    icon: '🌋',
    image: '/ui/passives/fire.webp',
    unlockLevels: [1, 3, 5],
    tiers: [
      'Each mission it joins that FAILS grants it +1 permanent 🔥.',
      'Each mission it joins that FAILS grants it +2 permanent 🔥.',
      'Each mission it joins that FAILS grants it +3 permanent 🔥.',
      'APOTHEOSIS: +6 permanent 🔥, and every creature on the failed mission gains it too.',
    ],
  },
  thornmail: {
    id: 'thornmail',
    name: 'Thornmail',
    icon: '🛡️',
    image: '/ui/passives/grass.webp',
    unlockLevels: [1, 2, 3],
    tiers: [
      'Failed missions it joins deal 1 less damage, and the failure still pays +2 XP.',
      'Failed missions it joins deal 2 less damage, and the failure still pays +2 XP.',
      'Failed missions it joins deal 3 less damage, and the failure still pays +2 XP.',
      'APOTHEOSIS: failures it joins deal NO damage at all, and still pay the XP.',
    ],
  },
  scrapfire: {
    id: 'scrapfire',
    name: 'Scrapfire',
    icon: '🔧',
    image: '/ui/passives/fire.webp',
    // ⚖️ v6.68 (per Javi: "can you nerf coalpip? currently too strong
    // maybe 1 5 9 for level distribution"). Was 1/3/5, which handed a
    // rare its whole ladder by Lv5 and its Apotheosis at Lv7 — in an
    // expiry meta where Scrapfire's growth is PERMANENT, that compounded
    // far too early. The magnitudes (+1/+2/+3, +6 at Apotheosis) are
    // untouched; they simply arrive on the standard pace, and the
    // Apotheosis follows the ladder to Lv11 (last natural + 2), where
    // the elemental five's own already sits.
    unlockLevels: [1, 5, 9],
    tiers: [
      'Whenever ANY mission on the field expires, it gains +1 permanent to its lowest element above 0.',
      'Whenever ANY mission on the field expires, it gains +2 permanent to its lowest element above 0.',
      'Whenever ANY mission on the field expires, it gains +3 permanent to its lowest element above 0.',
      'APOTHEOSIS: +6 permanent, and every expiry also feeds its HIGHEST element.',
    ],
  },
  grit: {
    id: 'grit',
    name: 'Grit',
    icon: '😤',
    image: '/ui/passives/earth.webp',
    unlockLevels: [2, 4, 6],
    tiers: [
      'Missions where it is the ONLY creature gain +2% chance per point of ⚔️ Fight it carries.',
      'Missions where it is the ONLY creature gain +4% chance per point of ⚔️ Fight it carries.',
      'Missions where it is the ONLY creature gain +6% chance per point of ⚔️ Fight it carries.',
      'APOTHEOSIS: +10% per ⚔️ point, and it counts as solo even with a full team beside it.',
    ],
  },
  rootstock: {
    id: 'rootstock',
    name: 'Rootstock',
    icon: '🌳',
    image: '/ui/passives/grass.webp',
    unlockLevels: [1, 9],
    tiers: [
      'Completing a node grants the whole team +2 XP.',
      'Completing a node grants the whole team +2 XP and +5 gold.',
      'APOTHEOSIS: rest stops also grant the whole team +1 permanent to every element.',
    ],
  },
  'deep-sit': {
    id: 'deep-sit',
    name: 'Deep Sit',
    icon: '🧘',
    image: '/ui/passives/earth.webp',
    unlockLevels: [2, 4, 6],
    tiers: [
      'Every 10s resting at home, its next mission gets +2 🪨 for that fight.',
      'Every 10s resting at home, its next mission gets +4 🪨 for that fight.',
      'Every 10s resting at home, its next mission gets +6 🪨 for that fight.',
      'APOTHEOSIS: +10 🪨, and the buff no longer expires after one mission — it holds until the node ends.',
    ],
  },
  'first-light': {
    id: 'first-light',
    name: 'First Light',
    icon: '🌄',
    image: '/ui/passives/fire.webp',
    unlockLevels: [3, 6, 9],
    tiers: [
      'The first mission dispatched each node that includes it gains +5% chance and resolves 20% faster.',
      'The first mission dispatched each node that includes it gains +10% chance and resolves 20% faster.',
      'The first mission dispatched each node that includes it gains +15% chance and resolves 20% faster.',
      'APOTHEOSIS: +15% and 40% faster — and it applies to the first mission of every node whether or not it joins.',
    ],
  },
  tailwind: {
    id: 'tailwind',
    name: 'Tailwind',
    icon: '💨',
    image: '/ui/passives/water.webp',
    unlockLevels: [1, 2, 3],
    tiers: [
      'Every creature dispatched after it travels +2 Speed faster for one mission.',
      'Every creature dispatched after it travels +3 Speed faster for one mission.',
      'Every creature dispatched after it travels +4 Speed faster for one mission.',
      'APOTHEOSIS: +6 Speed — and unlike the lower tiers, its own team rides the wind too.',
    ],
  },
  // ── 🌟 RARE tranche 2a (v2.87) ──────────────────────────────────
  forgeheart: {
    id: 'forgeheart', name: 'Forgeheart', icon: '🔨', image: '/ui/passives/fire.webp',
    unlockLevels: [3, 5, 7],
    tiers: [
      'Every 🔥 mission cleared anywhere gives it +1 permanent 🔥.',
      'Every 🔥 mission cleared anywhere gives it +2 permanent 🔥.',
      'Every 🔥 mission cleared anywhere gives it +3 permanent 🔥.',
      'APOTHEOSIS: +6 permanent, and EVERY mission cleared feeds it, not only 🔥 ones.',
    ],
  },
  'heat-death': {
    id: 'heat-death', name: 'Heat Death', icon: '☄️', image: '/ui/passives/fire.webp',
    unlockLevels: [5, 7, 9],
    tiers: [
      'Bosses and elites it defeats grant +1 permanent to ALL its elements.',
      'Bosses and elites it defeats grant +2 permanent to ALL its elements.',
      'Bosses and elites it defeats grant +3 permanent to ALL its elements.',
      'APOTHEOSIS: +6 to all elements per boss or elite, and act bosses count twice.',
    ],
  },
  kilnheart: {
    id: 'kilnheart', name: 'Kilnheart', icon: '⚒️', image: '/ui/passives/earth.webp',
    unlockLevels: [3, 5, 7, 9],
    tiers: [
      'Per Elite mission and boss defeated: +2 permanent 🪨.',
      'Per Elite mission and boss defeated: +2 permanent 🪨 and +2 🔥.',
      'Per Elite mission and boss defeated: +4 permanent to both.',
      'Elite and boss XP counts DOUBLE for it, on top of +4 to both.',
      'APOTHEOSIS: +8 to both per Elite/boss, and Elite & boss XP counts TRIPLE.',
    ],
  },
  soulbind: {
    id: 'soulbind', name: 'Soulbind', icon: '⛓️', image: '/ui/passives/grass.webp',
    unlockLevels: [3, 5, 7],
    tiers: [
      "When a mission it joins fails, it keeps 1 point of that mission's requirement as permanent stats.",
      "When a mission it joins fails, it keeps 2 points of that mission's requirement as permanent stats.",
      "When a mission it joins fails, it keeps 3 points of that mission's requirement as permanent stats.",
      "APOTHEOSIS: keeps the failed mission's ENTIRE requirement as permanent stats.",
    ],
  },
  prospector: {
    id: 'prospector', name: 'Prospector', icon: '💰', image: '/ui/passives/earth.webp',
    unlockLevels: [1, 5, 9],
    tiers: [
      'Missions it joins pay +5 gold.',
      'Missions it joins pay +10 gold.',
      'Missions it joins pay +15 gold.',
      'APOTHEOSIS: +40 gold, and gold missions it joins pay out a second time.',
    ],
  },
  'last-stand': {
    id: 'last-stand', name: 'Last Stand', icon: '🩸', image: '/ui/passives/earth.webp',
    // v4.75 (per Javi: "I don't think last stand has an apotheosis"):
    // it did — but the [5, 10, 20] ladder put it at level 22, which no
    // run reaches. v4.76 (per Javi: "5 10 15 17"): a slow ladder, but a
    // reachable one — the +2 rule lands the Apotheosis at 17 exactly.
    unlockLevels: [5, 10, 15],
    tiers: [
      'Below 40% HP it gains +5 to every element.',
      'Below 40% HP it gains +10 to every element.',
      'Below 40% HP it gains +20 to every element.',
      'APOTHEOSIS: +40 to every element, and the threshold rises to 60% HP.',
    ],
  },
  // 🔆 v3.53 (per Javi): Tidebeacon's passive, reflavored off the karaoke
  // stage. The id stays 'crowd-work' so saves and locale keys survive.
  'crowd-work': {
    id: 'crowd-work', name: 'Watchlight', icon: '🔆', image: '/ui/passives/water.webp',
    unlockLevels: [4, 8],
    tiers: [
      '+1% success chance for every creature keeping watch at camp.',
      '+2% success chance for every creature keeping watch at camp.',
      'APOTHEOSIS: +5% per watcher, and teams already out on missions still see the light.',
    ],
  },
  underdog: {
    id: 'underdog', name: 'Underdog', icon: '🐣', image: '/ui/passives/earth.webp',
    unlockLevels: [1, 5],
    tiers: [
      'If it is the lowest-level creature on the mission: +1 to everything.',
      'If it is the lowest-level creature on the mission: +2 to everything.',
      'APOTHEOSIS: +5 to everything, and it counts as the lowest-level creature even when it is not.',
    ],
  },
  reflection: {
    id: 'reflection', name: 'Reflection', icon: '🪞', image: '/ui/passives/water.webp',
    unlockLevels: [4, 8],
    // v4.66 (per Javi): ALL stats, not just the single highest — for
    // each element it mirrors the best teammate value. Still rounds UP.
    tiers: [
      'On its missions, copies 25% of each element\'s best teammate value.',
      'On its missions, copies 50% of each element\'s best teammate value.',
      // v4.86 (per Javi): the keep comes from the creatures it FOUGHT
      // BESIDE this node — partner it deliberately to pick the stat.
      // v4.88: a 25% share — 100% let two mirrors double each other.
      'APOTHEOSIS: copies 100% of every element, and at node end permanently keeps 25% of the best stat among the teammates it fought beside.',
    ],
  },
  'power-nap': {
    id: 'power-nap', name: 'Power Nap', icon: '😴', image: '/ui/passives/water.webp',
    unlockLevels: [2, 5],
    tiers: [
      '15s of unbroken rest at home grants +1 to all elements on its next trip.',
      '15s of unbroken rest at home grants +2 to all elements on its next trip.',
      'APOTHEOSIS: +5 to all elements, and the nap counts even while it is out on a mission.',
    ],
  },
  'gilded-scales': {
    id: 'gilded-scales', name: 'Gilded Scales', icon: '🐍', image: '/ui/passives/water.webp',
    unlockLevels: [2, 6, 10],
    tiers: [
      "Shop prices drop 10% while it's on the roster.",
      "Shop prices drop 20% while it's on the roster.",
      "Shop prices drop 30% while it's on the roster.",
      'APOTHEOSIS: shop prices drop 60%, and every shop stocks twice as many relics.',
    ],
  },
  'green-thumb': {
    id: 'green-thumb', name: 'Green Thumb', icon: '🌿', image: '/ui/passives/grass.webp',
    unlockLevels: [2, 6],
    // v4.66 (per Javi): arriving at a rest stop also grows max HP,
    // permanently, from tier 1.
    tiers: [
      "Rest stops heal +2 extra HP, and ARRIVING at one permanently grows your max HP by +2, while it's on the roster.",
      "Rest stops heal +4 extra HP, and arriving still grows max HP by +2.",
      'APOTHEOSIS: rest stops heal +15 extra, cure every injury, and arrival grows max HP by +4.',
    ],
  },
  // ── 🌟 RARE tranche 2b (v2.91) ──────────────────────────────────
  'second-wind': {
    id: 'second-wind', name: 'Second Wind', icon: '🌬️', image: '/ui/passives/grass.webp',
    unlockLevels: [2, 5],
    tiers: [
      'After returning home, its next dispatch this node gets +1 to ALL its elements.',
      'After returning home, its next dispatch this node gets +3 to ALL its elements.',
      'APOTHEOSIS: +6 to all its elements, on EVERY dispatch — not just the one after coming home.',
    ],
  },
  'static-charge': {
    id: 'static-charge', name: 'Static Charge', icon: '🔋', image: '/ui/passives/lightning.webp',
    unlockLevels: [3, 6, 9],
    tiers: [
      'Each mission it completes stores a charge; its next surge spends them all — 1 charge = +1 to all elements.',
      'Each completed mission stores a charge; spending them also cuts that surge’s cooldown by 5s per charge.',
      'Each completed mission stores a charge, and charges count DOUBLE when spent.',
      'APOTHEOSIS: charges are worth +3 each and are never spent — the surge keeps them.',
    ],
  },
  evergrowth: {
    id: 'evergrowth', name: 'Evergrowth', icon: '🌱', image: '/ui/passives/grass.webp',
    unlockLevels: [5, 7, 9],
    tiers: [
      'Each teammate that LEVELS UP grants it +1 permanent 🌿.',
      'Each teammate that LEVELS UP grants it +2 permanent 🌿.',
      'Each teammate that LEVELS UP grants it +3 permanent 🌿.',
      'APOTHEOSIS: +6 permanent 🌿 per teammate level-up, and it gains a level itself whenever three teammates do.',
    ],
  },
  resonance: {
    id: 'resonance', name: 'Resonance', icon: '🎶', image: '/ui/passives/lightning.webp',
    unlockLevels: [1, 3, 5],
    tiers: [
      'While it is dispatched, every OTHER active mission gains +1% chance per teammate on it.',
      'While it is dispatched, every OTHER active mission gains +3% chance per teammate on it.',
      'While it is dispatched, every OTHER active mission gains +5% chance per teammate on it.',
      'APOTHEOSIS: +6% per teammate, and it applies while it rests at home too.',
    ],
  },
  ballast: {
    id: 'ballast', name: 'Ballast', icon: '⚓', image: '/ui/passives/earth.webp',
    unlockLevels: [2, 4, 6],
    tiers: [
      'Missions with FULL slots gain +2% chance per creature aboard.',
      'Missions with FULL slots gain +4% chance per creature aboard.',
      'Missions with FULL slots gain +6% chance per creature aboard.',
      'APOTHEOSIS: +10% per creature aboard, and full-slot missions cannot fall below 50%.',
    ],
  },
  equanimity: {
    id: 'equanimity', name: 'Equanimity', icon: '☯️', image: '/ui/passives/water.webp',
    unlockLevels: [4, 6, 8],
    // v5.11 (per Javi): floating points, not virtual zeros — the pool
    // auto-fills whatever the assigned mission still lacks.
    tiers: [
      'Carries 2 floating points that auto-fill whatever its mission still lacks.',
      'Carries 4 floating points that auto-fill whatever its mission still lacks.',
      'Carries 6 floating points that auto-fill whatever its mission still lacks.',
      'APOTHEOSIS: 12 floating points fill the mission\'s gaps.',
    ],
  },
  // ── 🌟 RARE tranche 2c (v2.92) ──────────────────────────────────
  'will-o-wisp': {
    id: 'will-o-wisp', name: "Will-o'-Wisp", icon: '🏮', image: '/ui/passives/lightning.webp',
    unlockLevels: [4, 8],
    tiers: [
      "Each expired mission's ghost joins the Lantern: its next mission asks 2 less requirement per ghost.",
      "Each expired mission's ghost joins the Lantern: its next mission asks 4 less requirement per ghost.",
      'APOTHEOSIS: 6 less per ghost — and the ghosts are never spent; they haunt the whole node.',
    ],
  },
  // 🔥 v3.53 (per Javi): Briarflare's passive — the giggling is gone, the
  // spark spreads instead. Id unchanged, as with Watchlight above.
  'infectious-giggle': {
    id: 'infectious-giggle', name: 'Catchfire', icon: '🔥', image: '/ui/passives/grass.webp',
    unlockLevels: [3, 7],
    // v4.66 (per Javi): the spark BURNS IN now — permanent, once per node.
    tiers: [
      'Once per node, dispatching it surged burns the spark in: a random teammate PERMANENTLY gains +1 to the surge element.',
      'Once per node, dispatching it surged burns the FULL surge bonus into a random teammate permanently.',
      'APOTHEOSIS: the whole roster keeps the full surge bonus permanently.',
    ],
  },
  // ── 🌟 RARE tranche 2d (v2.93) — the tier's last two ─────────────
  featherweight: {
    id: 'featherweight', name: 'Featherweight', icon: '🪶', image: '/ui/passives/lightning.webp',
    unlockLevels: [2, 7],
    tiers: [
      'Once per node, it can join a mission whose slots are FULL — it takes no slot.',
      'It ALWAYS takes no slot — full missions are never full to it.',
      'APOTHEOSIS: it takes no slot AND grants one — every mission it joins fits one more creature.',
    ],
  },
  routine: {
    id: 'routine', name: 'Routine', icon: '🎭', image: '/ui/passives/water.webp',
    unlockLevels: [5, 9],
    // v4.68 (per Javi): the OPENING ACT — the node's very first deploy.
    tiers: [
      'Copies the very FIRST creature deployed each node at 75% — worn ON TOP of its own stats until the node ends.',
      'Copies the node\'s first-deployed creature at 100% — those stats plus its own.',
      'APOTHEOSIS: the copy is 150% — and the passive comes with it.',
    ],
  },
  // ── 💜 EPIC tranche 3a (v2.94) ──────────────────────────────────
  cremation: {
    id: 'cremation', name: 'Cremation', icon: '⚱️', image: '/ui/passives/fire.webp',
    unlockLevels: [2, 4, 8],
    tiers: [
      'Every mission that EXPIRES grants it +2 permanent 🔥 and +1 to its lowest element. Neglect becomes a strategy.',
      'Every mission that EXPIRES grants it +4 permanent 🔥 and +2 to its lowest.',
      'Every mission that EXPIRES grants it +8 permanent 🔥 and +4 to its lowest.',
      'APOTHEOSIS: +16 permanent 🔥 and +8 lowest per expiry — and expiries deal no damage at all.',
    ],
  },
  zenith: {
    id: 'zenith', name: 'Zenith', icon: '☀️', image: '/ui/passives/fire.webp',
    // v3.09 (per Javi, from the balance pass): 1/3/5 → 5/8/11. At +32.5pp
    // solo it was the second-strongest creature in the game — the tempo
    // engine is now earned, not innate.
    unlockLevels: [5, 8, 11],
    tiers: [
      'Its SUCCESSES count 1 extra toward the ⭐ goal.',
      'Its SUCCESSES count 2 extra toward the ⭐ goal.',
      'Its SUCCESSES count 3 extra toward the ⭐ goal.',
      'APOTHEOSIS: its successes count SIX extra toward the ⭐ goal.',
    ],
  },
  permafrost: {
    id: 'permafrost', name: 'Permafrost', icon: '🧊', image: '/ui/passives/water.webp',
    unlockLevels: [1, 5, 10],
    // v5.21 (per Javi): ONE freeze pool per NODE — absorbed chip damage
    // becomes stats until the pool runs dry, deployed or not. Only the
    // injury watch requires it home.
    tiers: [
      'Chip damage — wins, losses and expiries — freezes into its body before your ❤️: a pool of +1 💧 +1 🪨 +1 🌿 per NODE (💧 fills first), working even while deployed. While it rests at home, injuries cannot land.',
      'Chip damage freezes into its body before your ❤️: a pool of +2 💧 +2 🪨 +2 🌿 per node, working even while deployed. While it rests at home, injuries cannot land.',
      'Chip damage freezes into its body before your ❤️: a pool of +4 💧 +4 🪨 +4 🌿 per node, working even while deployed. While it rests at home, injuries cannot land.',
      'APOTHEOSIS: the freeze pool deepens to +6 💧 +6 🪨 +6 🌿 per node.',
    ],
  },
  reliquary: {
    id: 'reliquary', name: 'Reliquary', icon: '🏺', image: '/ui/passives/earth.webp',
    unlockLevels: [1, 3, 5],
    tiers: [
      'Every relic you own grants it +1 to its highest element AND its lowest above 0. Relic count becomes a stat.',
      'Every relic you own grants it +2 to its highest element and its lowest above 0.',
      'Every relic you own grants it +3 to its highest element and its lowest above 0.',
      'APOTHEOSIS: +6 per relic owned, spread across ALL its elements rather than two.',
    ],
  },
  'eclipse-bloom': {
    id: 'eclipse-bloom', name: 'Eclipse Bloom', icon: '🌘', image: '/ui/passives/grass.webp',
    // v3.17 (per Javi): 1/5/9 → 1/7/11. Even without the double-dip she
    // measured +49pp solo — the bloom scales too early, not too hard.
    // v3.68 (per Javi, final): battle nodes only at the ORIGINAL 1/7/11.
    // Four node-set × ladder combinations were measured; every restricted
    // one landed on the same −3pp in Normal and the same rank ~391 in the
    // Gauntlet, so the ladder is inert either way and the node set is the
    // only lever that moves anything. Javi picked the restricted side
    // knowing the Normal cost.
    unlockLevels: [1, 7, 11],
    tiers: [
      'At the END of each BATTLE node — fights, elites, bosses, puzzles and ambushes — ONE element blooms for +4 permanent, rotating 🔥→💧→🌿→⚡→🪨 node by node. BOSS nodes pay double. Rests, chests and shops give nothing, and it NEVER gains stat points from leveling.',
      'The blooming element gains +6 permanent per battle node (double on bosses), still rotating (no stat points from leveling).',
      'The blooming element gains +8 permanent per battle node (double on bosses), still rotating (no stat points from leveling).',
      'APOTHEOSIS: the blooming element gains +12 permanent per battle node — +24 on a boss (still no stat points from leveling).',
    ],
  },
  osmosis: {
    id: 'osmosis', name: 'Osmosis', icon: '💧', image: '/ui/passives/water.webp',
    unlockLevels: [2, 4, 6, 8],
    tiers: [
      'Gains +1 PERMANENT to its lowest AND highest elements every 5 successes.',
      'Gains +1 PERMANENT to its lowest and highest elements every 4 successes.',
      'Gains +1 PERMANENT to its lowest and highest elements every 3 successes.',
      'Gains +1 PERMANENT to its lowest and highest elements every 2 successes.',
      'APOTHEOSIS: +1 permanent to its lowest and highest on EVERY success.',
    ],
  },
  // ── 💜 EPIC tranche 3b (v2.95) ──────────────────────────────────
  'night-watch': {
    id: 'night-watch', name: 'Night Watch', icon: '🦉', image: '/ui/passives/fire.webp',
    unlockLevels: [1, 4, 7],
    tiers: [
      'While it rests at HOME, every mission on the field expires 10% slower.',
      'While it rests at HOME, every mission on the field expires 20% slower.',
      'While it rests at HOME, every mission on the field expires 30% slower.',
      'APOTHEOSIS: 50% slower expiry — and it works while it is OUT on a mission, not only resting.',
    ],
  },
  'bedrock-memory': {
    id: 'bedrock-memory', name: 'Bedrock Memory', icon: '🗿', image: '/ui/passives/earth.webp',
    unlockLevels: [1, 6, 11],
    tiers: [
      'Each UNSPENT pending stat point grants +1 temporary to all elements (max 1).',
      'Each UNSPENT pending stat point grants +1 temporary to all elements (max 3).',
      'Each UNSPENT pending stat point grants +1 temporary to all elements, uncapped.',
      'APOTHEOSIS: each unspent point grants +2 to all elements — and SPENDING them keeps the bonus.',
    ],
  },
  'hibernal-might': {
    id: 'hibernal-might', name: 'Hibernal Might', icon: '🐻', image: '/ui/passives/earth.webp',
    // v4.66 (Javi's balance pass, tuned together): the sleep BANKS
    // permanent stats now — once per node, so bench time can't print
    // infinitely. Unlocks pulled up from 8/12.
    unlockLevels: [5, 9],
    // v5.33 (per Javi's banked focus rebalance): 30s at every tier.
    // v5.38 (per Javi): the bank is GRASS-heavy — "6 grass 3 stone",
    // literally.
    tiers: [
      '30s of unbroken rest at home BANKS +6 🌿 and +3 🪨 permanently — once per node.',
      '30s of unbroken rest at home banks +12 🌿 and +6 🪨 permanently — once per node.',
      'APOTHEOSIS: 30s of rest banks +18 🌿 and +9 🪨 permanently — once per node.',
    ],
  },
  refraction2: {
    id: 'refraction2', name: 'Prismatic Refraction', icon: '🔮', image: '/ui/passives/water.webp',
    unlockLevels: [6, 9, 12],
    tiers: [
      'Element charms count DOUBLE for it.',
      'Element charms count TRIPLE for it.',
      'Element charms count TRIPLE, and its surges gain +1 per owned charm.',
      'APOTHEOSIS: charms count QUADRUPLE, and its surges gain +2 per owned charm.',
    ],
  },
  overgrowth: {
    id: 'overgrowth', name: 'Overgrowth', icon: '🌵', image: '/ui/passives/grass.webp',
    unlockLevels: [2, 3, 5],
    tiers: [
      'Missions it joins gain +1 slot, and each extra body adds +2% chance.',
      'Missions it joins gain +1 slot, and each extra body adds +3% chance.',
      'Missions it joins gain +1 slot, and each extra body adds +5% chance.',
      'APOTHEOSIS: +2 slots, and +10% chance per extra body.',
    ],
  },
  // ── 💜 EPIC tranche 3c (v2.96) ──────────────────────────────────
  'moonlit-mending': {
    id: 'moonlit-mending', name: 'Moonlit Mending', icon: '🌙', image: '/ui/passives/grass.webp',
    // v4.66 (per Javi): 5/7/9 → 3/5/7 — the anti-injury kit comes online
    // while injuries still hurt.
    unlockLevels: [3, 5, 7],
    tiers: [
      'At node end, cures 1 injury stack team-wide.',
      'Each success draws from a 5-marble bag: the winning marble cures 1 injury stack and heals you its LEVEL in HP.',
      'Its teammates cannot STACK injuries at all — one is the most they ever carry.',
      'APOTHEOSIS: injuries cannot land on the roster at all.',
    ],
  },
  sporebank: {
    id: 'sporebank', name: 'Sporebank', icon: '🍄', image: '/ui/passives/grass.webp',
    unlockLevels: [2, 7, 11],
    tiers: [
      'Gains +5 XP whenever a TEAMMATE levels up.',
      'Also gains +1 XP per mission cleared anywhere.',
      'When IT levels up, the whole team gains +10 XP.',
      'APOTHEOSIS: +2 XP per mission cleared, and when it levels the whole team LEVELS with it.',
    ],
  },
  'thorn-toll': {
    id: 'thorn-toll', name: 'Thorn Toll', icon: '🦔', image: '/ui/passives/earth.webp',
    unlockLevels: [3, 6, 9],
    // v5.14 (per Javi: "should be better for an epic"): always-on field
    // aura — no priming, no aboard requirement.
    tiers: [
      'Its quills blanket the field: EVERY mission asks 2 less total requirement.',
      'Its quills blanket the field: EVERY mission asks 4 less total requirement.',
      'Its quills blanket the field: EVERY mission asks 6 less total requirement.',
      'APOTHEOSIS: every mission asks 12 less total requirement.',
    ],
  },
  // 🪙 v5.16 (per Javi): Sable Corsair's custom passive — the first
  // skill-carrier to also hold a passive, built to feed Plunder.
  'buried-treasure': {
    id: 'buried-treasure', name: 'Buried Treasure', icon: '🪙', image: '/ui/passives/water.webp',
    unlockLevels: [3, 6, 9],
    tiers: [
      'At every node\'s end, the hoard grows: +1 💰 per 20 gold held.',
      'At every node\'s end, the hoard grows: +1 💰 per 15 gold held.',
      'At every node\'s end, the hoard grows: +1 💰 per 10 gold held.',
      'APOTHEOSIS: +1 💰 per 5 gold held — the chest all but doubles a patient fortune.',
    ],
  },
  'refraction-cascade': {
    id: 'refraction-cascade', name: 'Refraction Cascade', icon: '🌈', image: '/ui/passives/water.webp',
    unlockLevels: [1, 3, 5],
    tiers: [
      'Its Surge applies to the WHOLE team.',
      'Its Surge applies to the WHOLE team, and the surge bonus is DOUBLED.',
      'Its Surge applies to the WHOLE team, and the surge bonus is TRIPLED.',
      'APOTHEOSIS: a team-wide surge at 5× bonus — and it never goes on cooldown.',
    ],
  },
  weathered: {
    id: 'weathered', name: 'Weathered', icon: '🪨', image: '/ui/passives/earth.webp',
    unlockLevels: [6, 10],
    tiers: [
      "Bulwark's lockout drops from 20s to 12s.",
      'A Bulwarked FAILURE still pays half XP.',
      'APOTHEOSIS: Bulwark has no lockout at all, and a Bulwarked failure pays FULL XP.',
    ],
  },
  bioluminance: {
    id: 'bioluminance', name: 'Bioluminance', icon: '🪼', image: '/ui/passives/water.webp',
    unlockLevels: [5, 11],
    tiers: [
      'Luminesce also applies at chests and rest stops.',
      'Luminesce also applies at Elite and boss reward sequences.',
      'APOTHEOSIS: every reward pick in the run offers 5 cards — no arming needed.',
    ],
  },
  // ── 🧡 LEGENDARY tranche 4a (v3.03) ─────────────────────────────
  'dragons-hoard': {
    id: 'dragons-hoard', name: "Dragon's Hoard", icon: '🐲', image: '/ui/passives/earth.webp',
    unlockLevels: [3, 7, 11],
    tiers: [
      '+2 to ALL elements per 25 gold HELD (cap +4).',
      '+2 to ALL elements per 25 gold HELD (cap +6).',
      '+2 to ALL elements per 25 gold HELD, uncapped.',
      'APOTHEOSIS: +4 per 25 gold, uncapped — and gold spent still counts for the rest of the node.',
    ],
  },
  // 🌊 v3.59 buff (per Javi): unlocks 6/9/12 → 3/6/9, and the team-wide
  // surge now sends the team out faster and hitting harder.
  'tide-deluge': {
    id: 'tide-deluge', name: 'Deluge', icon: '🌊', image: '/ui/passives/water.webp',
    unlockLevels: [3, 6, 9],
    // v4.66 (per Javi, tuned together): her surges STACK for the node —
    // each cast adds a lasting layer to her missions. Apotheosis keeps
    // the 10s cooldown (stacking + no cooldown would loop forever).
    tiers: [
      'Her Surge applies to the WHOLE team and STACKS — every cast adds a lasting layer to her missions this node. The team carries +1 👟 Speed and +2 ⚔️ Fight.',
      'Each team-wide layer is +2 stronger, and the rider grows to +2 👟 Speed and +4 ⚔️ Fight.',
      'Her Surge cooldown drops to 10 seconds.',
      'APOTHEOSIS: each layer is +5 stronger. The cooldown stays 10 seconds.',
    ],
  },
  // 🔥 v3.59 buff (per Javi): per NODE from level 1 (was per act), and
  // the level-9 tier pays the whole roster for the save.
  rekindle: {
    id: 'rekindle', name: 'Rekindle', icon: '🔥', image: '/ui/passives/fire.webp',
    unlockLevels: [1, 9],
    tiers: [
      "Once per NODE: lethal damage leaves you at 1 HP instead, and the whole roster's injuries burn away.",
      'Once per NODE, and every creature gains +1 to ALL elements permanently when it fires.',
      'APOTHEOSIS: lethal damage leaves you at FULL HP, once per node.',
    ],
  },
  // 🌱 v3.59 buff (per Javi): EVERY level pays now (the v3.09 even-level
  // gate is gone), and the ladder stretches 1/5/9/13 → 1/5/11/19.
  millennia: {
    id: 'millennia', name: 'Millennia', icon: '🌱', image: '/ui/passives/grass.webp',
    unlockLevels: [1, 5, 11, 19],
    // v5.33 (per Javi's banked focus rebalance): the ages feed its TWO
    // HIGHEST elements — build-defining instead of a flat rainbow.
    tiers: [
      'EVERY level feeds the ages: +1 to its HIGHEST element and +1 to its second-highest.',
      'Also feeds them once more per ACT completed.',
      'Its levels cost 25% less XP.',
      'Its growth levels grant +2 to the highest and +1 to the second instead.',
      'APOTHEOSIS: level-ups grant +4 to the highest and +2 to the second, and it levels on HALF XP.',
    ],
  },
  'eternal-return': {
    id: 'eternal-return', name: 'Eternal Return', icon: '♾️', image: '/ui/passives/water.webp',
    // v5.32 (per Javi's correction of v5.30): THREE naturals at 3/9/15
    // and the fourth number he listed — 21 — is the APOTHEOSIS itself
    // (apotheosisAt pins it; the generic rule would say 17). The
    // act-end bonus stays gone.
    unlockLevels: [3, 9, 15],
    apotheosisAt: 21,
    tiers: [
      'When a node ENDS, every permanent stat point the roster gained during it is granted AGAIN.',
      'When a node ENDS, the roster’s permanent gains from it are granted again TWICE over.',
      'When a node ENDS, the roster’s permanent gains from it are granted again THREE times over.',
      'APOTHEOSIS: the node’s permanent gains are granted FIVE times over.',
    ],
  },
  'mirri-understudy': {
    id: 'mirri-understudy', name: 'Understudy', icon: '🪶', image: '/ui/passives/lightning.webp',
    // v4.70 (per Javi): 2/4/6/8/10 known — 12 at the Apotheosis. The
    // crow never rolls the TRAINED skill of the same name (the copy
    // would loop).
    unlockLevels: [2, 4, 6, 8, 10],
    tiers: [
      'Knows TWO random trained skills innately — the only normal-mode source of trained skills.',
      'Knows FOUR random trained skills.',
      'Knows SIX random trained skills.',
      'Knows EIGHT random trained skills.',
      'Knows TEN random trained skills.',
      'APOTHEOSIS: knows TWELVE random trained skills, and re-rolls one of them each act.',
    ],
  },
  bloodbloom: {
    id: 'bloodbloom', name: 'Bloodbloom', icon: '🌹', image: '/ui/passives/grass.webp',
    unlockLevels: [5, 8, 11],
    tiers: [
      'Knows Blood Pact innately, and her pacts cost 1 HP instead of 2.',
      'Her pacts give +15% success chance.',
      'On her missions the whole team pacts together — the cost and the bonus both scale with every teammate aboard.',
      'APOTHEOSIS: pacts cost NO HP and give +25%.',
    ],
  },
  // 👑 v3.59 buff (per Javi): Decree itself now WINS the mission and pays
  // FULL XP, so Sovereign's old half-XP/full-XP ladder had nothing left to
  // give. One tier at 11 doubles that XP instead.
  sovereign: {
    id: 'sovereign', name: 'Sovereign', icon: '👑', image: '/ui/passives/fire.webp',
    unlockLevels: [11],
    tiers: [
      'Decreed missions pay DOUBLE XP.',
      'APOTHEOSIS: Decree twice per node, and decreed missions count DOUBLE toward the ⭐ goal.',
    ],
  },
}


/**
 * Numeric knobs per tier, Apotheosis LAST — the mirror of
 * `PASSIVE_VALUES` for the elemental five. Effects read these by tier
 * index so the balance pass is a data edit, not a code edit.
 */
export const CREATURE_PASSIVE_VALUES = {
  /** 👑 Sovereign: XP MULTIPLIER on a decreed mission. Base Decree pays
   * full XP on its own (v3.59), so this doubles it. */
  sovereignXpShare: [2, 2],
  /** 🪶 Understudy: how many random trained skills it knows. */
  understudyKnown: [2, 4, 6, 8, 10, 12],
  /** 🌹 Bloodbloom: pact HP cost and chance bonus per tier. */
  bloodbloomCost: [1, 1, 1, 0],
  bloodbloomBonus: [10, 15, 15, 25],
  /** 🌱 Millennia: all-element gain per level-up. */
  millenniaPerLevel: [1, 1, 1, 2, 4],
  /** 🌱 XP-cost multiplier for its levels. */
  millenniaXpMult: [1, 1, 0.75, 0.75, 0.5],
  /** 🔥 Rekindle: all-element points every creature gains when it fires. */
  rekindleAllElements: [0, 1, 1],
  /** ♾️ Eternal Return: how many times node gains are re-granted. */
  eternalReturn: [1, 2, 3, 5],
  /** 🐲 Dragon's Hoard: bonus per 25 gold / the cap (Infinity = none). */
  hoardPer: [2, 2, 2, 4],
  hoardCap: [4, 6, Infinity, Infinity],
  /** 🌊 Tide Deluge: extra surge strength per tier. */
  tideDelugeBonus: [0, 2, 2, 5],
  /** 🌊 Cooldown CAP in seconds from tier 3 (v3.59: an absolute 10s, not
   * a subtraction). v4.66: Apotheosis keeps the 10s floor — stacking
   * surges with NO cooldown would loop forever. */
  tideDelugeCooldownCap: [Infinity, Infinity, 10, 10],
  /** 🌊 Speed and Fight the team-wide surge rides into the mission. */
  tideDelugeSpeed: [1, 2, 2, 2],
  tideDelugeFight: [2, 4, 4, 4],
  /** 🪨 Weathered: Bulwark's lockout per tier (base 20 without it). */
  weatheredLockout: [12, 12, 0],
  /** 🪨 XP share a Bulwarked failure still pays (base 0 without T2). */
  weatheredXpShare: [0, 0.5, 1],
  /** 🦔 Thorn Toll: requirement softened after a win. */
  thornToll: [2, 4, 6, 12],
  /** 🪙 Buried Treasure: gold held per +1 interest at node end. */
  buriedTreasureRate: [20, 15, 10, 5],
  /** 🌈 Refraction Cascade: team-wide surge multiplier. */
  cascadeMult: [1, 2, 3, 5],
  /** 🌙 Moonlit Mending: cure chance on its successes (tier 2+). */
  /** 🍄 Sporebank: XP per teammate level-up / per mission cleared. */
  sporebankTeammateXp: 5,
  sporebankMissionXp: [0, 1, 1, 2],
  /** 🍄 Team XP when IT levels (tier 3; Apo levels them instead). */
  sporebankTeamXp: 10,
  /** 🦉 Night Watch: expiry slowdown share while it keeps watch. */
  nightWatch: [0.1, 0.2, 0.3, 0.5],
  /** 🗿 Bedrock Memory: cap on counted points (Infinity = uncapped). */
  bedrockCap: [1, 3, Infinity, Infinity],
  /** 🐻 Hibernal Might: PERMANENT all-element bank after a full
   * hibernation, once per node (v4.66 redesign). */
  /** 🐻 v5.33 (per Javi): the bank is earth-heavy, not +all.
   * v5.38 (per Javi, correcting my v5.33 read): GRASS-heavy — he said
   * "6 grass 3 stone" and meant it literally. */
  hibernalGrass: [6, 12, 18],
  hibernalEarth: [3, 6, 9],
  /** 🐻 Seconds of unbroken rest a hibernation needs (v4.67: 60s — at
   * 20s the bank out-earned Duskmoth for free). */
  // v5.33 (per Javi): 30s at every tier.
  hibernalSeconds: [30, 30, 30],
  /** 🔮 Prismatic Refraction: what each charm counts as for it. */
  refractionMult: [2, 3, 3, 4],
  /** 🔮 Surge bonus per owned charm (tier 3+). */
  refractionSurge: [0, 0, 1, 2],
  /** 🌵 Overgrowth: extra slots granted / chance per extra body. */
  overgrowthSlots: [1, 1, 1, 2],
  overgrowthPerBody: [2, 3, 5, 10],
  /** ⚱️ Cremation: permanent 🔥 per expiry anywhere. */
  cremation: [2, 4, 8, 16],
  /** ⚱️ v4.66 (per Javi): the same expiry also feeds its LOWEST element. */
  cremationLow: [1, 2, 4, 8],
  /** ☀️ Zenith: extra ⭐ goal credit per success. */
  zenith: [1, 2, 3, 6],
  /** 🧊 Permafrost (v5.15): the per-element payout pool a loss offers —
   * chip damage eats it first, the survivors land as permanent stats. */
  permafrostPool: [1, 2, 4, 6],
  /** 🏺 Reliquary: per-relic bonus to its highest element. */
  reliquary: [1, 2, 3, 6],
  /** 🌘 Eclipse Bloom: permanent all-element gain at node end. */
  /** 🌘 v5.33 (per Javi): one rotating element, paid big. */
  eclipseBloom: [4, 6, 8, 12],
  /** 💧 Osmosis: successes needed per permanent point (1 = every). */
  osmosisEvery: [5, 4, 3, 2, 1],
  /** 🎭 Routine: the share of the copied statline it wears. */
  routine: [0.75, 1, 1.5],
  /** 🏮 Will-o'-Wisp: virtual requirement coverage per banked ghost. */
  willOWisp: [2, 4, 6],
  /** 🔥 Catchfire: element bonus a caught teammate receives. */
  giggle: [1, 1, 1],
  /** 🔥 Cooldown shaved off a caught teammate's surge (tier 2+). */
  giggleCooldown: [0, 5, 9999],
  /** 🌬️ Second Wind: bonus to ALL its elements on the primed dispatch. */
  secondWind: [1, 3, 6],
  /** 🔋 Static Charge: element bonus per stored charge when surging. */
  staticChargeWorth: [1, 1, 2, 3],
  /** 🌱 Evergrowth: permanent 🌿 per teammate level-up. */
  evergrowth: [1, 2, 3, 6],
  /** 🎶 Resonance: chance per teammate on the OTHER mission. */
  resonance: [1, 3, 5, 6],
  /** ⚓ Ballast: chance per creature aboard a full-slot mission. */
  ballast: [2, 4, 6, 10],
  /** ☯️ Equanimity: virtual coverage for each element it lacks. */
  equanimity: [2, 4, 6, 12],
  /** 🔨 Forgeheart: permanent 🔥 per 🔥 mission cleared. */
  forgeheart: [1, 2, 3, 6],
  /** ☄️ Heat Death: permanent to ALL elements per boss/elite. */
  heatDeath: [1, 2, 3, 6],
  /** ⚒️ Kilnheart: permanent 🪨 (and 🔥 from tier 2) per Elite/boss. */
  kilnheart: [2, 2, 4, 4, 8],
  /** ⛓️ Soulbind: requirement points kept on a failure (99 = all). */
  soulbind: [1, 2, 3, 99],
  /** 💰 Prospector: bonus gold per mission it joins. */
  prospector: [5, 10, 15, 40],
  /** 🩸 Last Stand: bonus to every element while badly hurt. */
  lastStand: [5, 10, 20, 40],
  /** 🩸 Last Stand: the HP share it triggers below. */
  lastStandThreshold: [0.4, 0.4, 0.4, 0.6],
  /** 🎤 Crowd Work: chance per creature watching from home. */
  crowdWork: [1, 2, 5],
  /** 🐣 Underdog: bonus while it is the lowest-level creature aboard. */
  underdog: [1, 2, 5],
  /** 🪞 Reflection: share of a teammate's best stat that it copies. */
  reflection: [0.25, 0.5, 1],
  /** 🪞 v4.88 (per Javi: Echopus + Understudy = "infinite copy of each
   * other stats"): the apo keeper banks this share of the best partner
   * stat, rounded up — 100% let two mirrors double each other every
   * node. */
  reflectionKeep: 0.25,
  /** 😴 Power Nap: bonus to all elements after a full nap. */
  powerNap: [1, 2, 5],
  /** 😴 Seconds of unbroken rest a nap needs. */
  powerNapSeconds: 15,
  /** 🐍 Gilded Scales: shop discount share. */
  gildedScales: [0.1, 0.2, 0.3, 0.6],
  /** 🌿 Green Thumb: extra HP from rest stops. */
  greenThumb: [2, 4, 15],
  /** Team XP granted on node completion. */
  rootstockXp: [2, 2, 2],
  /** Team gold granted on node completion (tier 2+). */
  rootstockGold: [0, 5, 5],
  /** How many missions per encounter get the expire bonus (Infinity = all). */
  morningChorusCount: [1, 2, 3, Infinity],
  /** Seconds added to those missions. */
  morningChorusSeconds: [10, 10, 10, 6],
  /** Permanent 🔥 per failed mission it joined. */
  ashbed: [1, 2, 3, 6],
  /** 🪨 granted per 10s rested, for its next fight. */
  deepSit: [2, 4, 6, 10],
  /** Success-chance bonus on the node's first dispatch. */
  firstLightChance: [5, 10, 15, 25],
  /** Fight-timer reduction on that mission. */
  firstLightHaste: [0.2, 0.2, 0.2, 0.4],
  /** Failure damage reduction on missions it joins. */
  thornmail: [1, 2, 3, 99],
  /** Bonus XP a failure still pays, every tier. */
  thornmailXp: 2,
  /** Speed granted to later dispatches this node. */
  tailwind: [2, 3, 4, 6],
  /** Permanent points per mission expiry. */
  scrapfire: [1, 2, 3, 6],
  /** Chance per ⚔️ Fight point when solo. */
  grit: [2, 4, 6, 10],
} as const

export function getCreaturePassive(id: string | undefined): PassiveDef | null {
  return id ? CREATURE_PASSIVES[id] ?? null : null
}
